import { useEffect, useState } from 'react';

/**
 * Standalone diagnostic page — minimal dependencies, no shared layout.
 * Used to debug VPN/CDN/firewall issues by showing what the server sees
 * about the user's request.
 *
 * Open this page WITHOUT VPN first → note the values.
 * Open this page WITH VPN enabled → compare. Differences = where the block is.
 *
 * If the page fails to load AT ALL with VPN → the block is BEFORE reaching
 * our backend (Cloudflare / hosting firewall / DNS).
 */
export default function DiagnosticPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [browserInfo, setBrowserInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [externalIp, setExternalIp] = useState(null);

  useEffect(() => {
    // Collect browser-side info
    const start = Date.now();
    setBrowserInfo({
      user_agent: navigator.userAgent,
      language: navigator.language,
      languages: navigator.languages?.join(', '),
      screen: `${window.screen.width}×${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: navigator.onLine,
      cookie_enabled: navigator.cookieEnabled,
      do_not_track: navigator.doNotTrack,
      hardware_concurrency: navigator.hardwareConcurrency,
      url: window.location.href,
    });

    // Call our backend diagnostic
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backend}/api/diagnostic`)
      .then((r) => r.json())
      .then((d) => {
        d.browser_to_backend_ms = Date.now() - start;
        setData(d);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });

    // Try a public IP service to see the client's actual external IP (might
    // not work behind some VPNs — that itself is useful info)
    fetch('https://api.ipify.org?format=json', { timeout: 5000 })
      .then((r) => r.json())
      .then((d) => setExternalIp(d.ip))
      .catch(() => setExternalIp('unreachable'));
  }, []);

  const Row = ({ k, v }) => (
    <div style={{ display: 'flex', borderBottom: '1px solid #444', padding: '6px 0', fontSize: 13 }}>
      <div style={{ width: 220, color: '#aaa', fontFamily: 'monospace' }}>{k}</div>
      <div style={{ flex: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v ?? '—')}</div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B3D2E',
      color: '#F5EFE0',
      padding: 20,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ color: '#D4AF37', fontSize: 24, marginBottom: 6 }}>🔍 WINLUXOR Diagnostic</h1>
        <p style={{ color: '#aaa', fontSize: 13, marginBottom: 20 }}>
          Cette page montre ce que voit le serveur sur votre connexion. Utile pour debug VPN/CDN/firewall.
        </p>

        {/* Status */}
        <div style={{
          padding: 16,
          borderRadius: 12,
          background: error ? '#7f1d1d' : (data ? '#065f46' : '#374151'),
          marginBottom: 20,
        }}>
          {loading && <span>⏳ Test en cours…</span>}
          {!loading && error && <span>❌ Erreur : {error}</span>}
          {!loading && data && <span>✅ Serveur joignable ({data.browser_to_backend_ms} ms)</span>}
        </div>

        {/* Browser info */}
        <div style={{ background: '#1f2937', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <h2 style={{ color: '#D4AF37', fontSize: 16, marginBottom: 10 }}>Navigateur (ce que VOUS voyez)</h2>
          {Object.entries(browserInfo).map(([k, v]) => <Row key={k} k={k} v={v} />)}
          <Row k="external_ip (ipify)" v={externalIp} />
        </div>

        {/* Backend info */}
        {data && (
          <div style={{ background: '#1f2937', borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <h2 style={{ color: '#D4AF37', fontSize: 16, marginBottom: 10 }}>Serveur (ce que LE SERVEUR voit)</h2>
            <Row k="client_ip_direct" v={data.client_ip_direct} />
            <Row k="cf_connecting_ip" v={data.cf_connecting_ip} />
            <Row k="cf_country" v={data.cf_country} />
            <Row k="cf_ray" v={data.cf_ray} />
            <Row k="x_forwarded_for" v={data.x_forwarded_for} />
            <Row k="x_real_ip" v={data.x_real_ip} />
            <Row k="user_agent" v={data.user_agent} />
            <Row k="host" v={data.host} />
            <Row k="origin" v={data.origin} />
            <Row k="referer" v={data.referer} />
            <Row k="accept_language" v={data.accept_language} />
            <Row k="server_time_utc" v={data.server_time_utc} />
          </div>
        )}

        {/* Comparison advice */}
        <div style={{ background: '#1e293b', borderRadius: 12, padding: 16, marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          <h2 style={{ color: '#D4AF37', fontSize: 15, marginBottom: 10 }}>💡 Comment utiliser cette page</h2>
          <ol style={{ paddingLeft: 18, color: '#cbd5e1' }}>
            <li>Ouvrez cette page <strong>SANS VPN</strong> → faites une capture d'écran complète</li>
            <li>Activez votre VPN puis rechargez cette page → si la page se charge, faites une nouvelle capture</li>
            <li>Si la page ne se charge PAS avec VPN → le blocage est <strong>AVANT</strong> notre backend (Cloudflare, hébergeur, ou DNS)</li>
            <li>Si elle se charge mais que l'<code>external_ip</code> diffère, c'est attendu (le VPN change votre IP)</li>
            <li>Si <code>cf_country</code> diffère, c'est aussi attendu</li>
            <li>Envoyez les 2 captures à l'admin → analyse précise</li>
          </ol>
        </div>

        {/* All headers */}
        {data?.all_headers && (
          <details style={{ background: '#1f2937', borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <summary style={{ color: '#D4AF37', cursor: 'pointer', fontSize: 13 }}>Tous les headers HTTP reçus par le serveur</summary>
            <pre style={{ marginTop: 12, fontSize: 11, overflow: 'auto', maxHeight: 300, color: '#cbd5e1' }}>
              {JSON.stringify(data.all_headers, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
